/**
 * SessionMoodPanel — in-session mood capture on the Deliver session screen (VER-118).
 *
 * Renders one 1–5 selector per enrolled participant and saves each independently, so a
 * facilitator can record as the group answers rather than filling a form and submitting at the
 * end — an interrupted session still keeps whatever was captured.
 *
 * Sessions 2–5 only. S1 and S6 carry the validated pre/post administrations, and a holistic
 * rating recorded at exactly the two points the outcome analysis pairs on is how a lightweight
 * check-in ends up being read as a clinical result. The server enforces this; the check here only
 * decides whether the panel appears at all, and MUST NOT be treated as the guard.
 *
 * Saving is per-participant and idempotent: the endpoint upserts on (participant, session), so
 * correcting a rating replaces it rather than adding a second reading for the same moment.
 *
 * The panel opens showing whatever is already stored for the session (GET /mood-check-ins), so a
 * facilitator revisiting it can see what was recorded rather than a blank scale they must guess at.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Card, CardContent, Divider, Grid, Typography } from "@mui/material";
import { Participant } from "common/build/prisma/client";
import * as participantsApi from "../../../client/api/participants";
import { listSessionMoodCheckIns, recordMoodCheckIn } from "../../../client/api/moodCheckIn";
import { useAuth } from "../../../contexts/auth.context";
import Spinner from "../../layout/Spinner";
import { MoodScaleSelector } from "./MoodScaleSelector";
import { MoodScore } from "./mood.types";

/** Mirrors MOOD_FIRST_SESSION / MOOD_LAST_SESSION in routes/api/moodCheckIn.ts. */
export const MOOD_FIRST_SESSION = 2;
export const MOOD_LAST_SESSION = 5;

export const isMoodSession = (sessionNumber: number): boolean =>
    sessionNumber >= MOOD_FIRST_SESSION && sessionNumber <= MOOD_LAST_SESSION;

type SessionMoodPanelProps = {
    sessionId: number;
    programmeId: number;
    sessionNumber: number;
};

/**
 * The participant list endpoint includes the join row when queried by programmeId, and consent is
 * held there rather than on the participant — a person may have withdrawn on one programme and not
 * another. The `include` is already narrowed to this programme, so there is at most one entry.
 */
export type ParticipantWithEnrolment = Participant & {
    ParticipantToProgramme?: { hasOptedOutOfSurveys: boolean }[];
};

type SaveState = "idle" | "saving" | "saved" | "error";

export const SessionMoodPanel = ({
    sessionId,
    programmeId,
    sessionNumber,
}: SessionMoodPanelProps): JSX.Element | null => {
    const { hasPermission, user } = useAuth();
    const canRecord = hasPermission("SurveyRespond") && !user?.isSchool;

    const [participants, setParticipants] = useState<ParticipantWithEnrolment[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [scores, setScores] = useState<Record<number, MoodScore>>({});
    const [saveStates, setSaveStates] = useState<Record<number, SaveState>>({});

    const applies = isMoodSession(sessionNumber) && canRecord;

    useEffect(() => {
        if (!applies) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        // Participants and any readings already stored for this session are fetched together: a
        // revisited session must open showing what was recorded, not blank. A blank panel over
        // stored data invites the two wrong responses — re-enter from memory, or assume it failed.
        Promise.all([participantsApi.list({ programmeId, take: 200 }), listSessionMoodCheckIns(sessionId)])
            .then(([page, existing]) => {
                if (cancelled) return;
                setScores(
                    existing.reduce<Record<number, MoodScore>>(
                        (accumulated, reading) => ({
                            ...accumulated,
                            [reading.participantId]: reading.score as MoodScore,
                        }),
                        {},
                    ),
                );
                // Consent: never offer a mood selector for a participant who has withdrawn from
                // surveys on this programme. The API refuses the write too — this is the affordance
                // half, so a practitioner is not invited to record something that will be rejected.
                // The include is already filtered to this programme, so index 0 is this enrolment.
                setParticipants(
                    (page.items as ParticipantWithEnrolment[]).filter(
                        (p) => !p.ParticipantToProgramme?.[0]?.hasOptedOutOfSurveys,
                    ),
                );
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setLoadError(error instanceof Error ? error.message : "The mood check-in could not be loaded.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [applies, programmeId, sessionId]);

    // Nothing to say on S1/S6, and nothing to offer a user who may not record a response.
    if (!applies) {
        return null;
    }

    const save = async (participantId: number, score: MoodScore) => {
        setScores((previous) => ({ ...previous, [participantId]: score }));
        setSaveStates((previous) => ({ ...previous, [participantId]: "saving" }));
        try {
            await recordMoodCheckIn({ participantId, programmeId, sessionId, score });
            setSaveStates((previous) => ({ ...previous, [participantId]: "saved" }));
        } catch {
            // Per-participant, so one failure never discards the ratings already saved for others.
            setSaveStates((previous) => ({ ...previous, [participantId]: "error" }));
        }
    };

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Mood check-in
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                    A quick 1–5 rating for each participant at this session. Captured during delivery — it is not a
                    clinical scale and is reported separately from survey scores.
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {loading ? (
                    <Spinner />
                ) : loadError ? (
                    <Alert severity="error">{loadError}</Alert>
                ) : participants.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No participants are enrolled on this programme yet.
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {participants.map((participant) => {
                            const state = saveStates[participant.id] ?? "idle";
                            return (
                                <Grid item xs={12} key={participant.id}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="body1">{participant.name}</Typography>
                                        {state === "saving" ? (
                                            <Typography variant="caption" color="textSecondary">
                                                Saving…
                                            </Typography>
                                        ) : state === "saved" ? (
                                            <Typography variant="caption" color="success.main">
                                                Saved
                                            </Typography>
                                        ) : state === "error" ? (
                                            <Typography variant="caption" color="error.main">
                                                Not saved — select again to retry
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <MoodScaleSelector
                                        value={scores[participant.id] ?? null}
                                        onChange={(score) => void save(participant.id, score)}
                                        disabled={state === "saving"}
                                        ariaLabel={`Mood rating for ${participant.name}`}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </CardContent>
        </Card>
    );
};
